// ⬡B:aba_shared.api_core:CODE:ababase_adapter:20260420⬡
// ⬡B:aba_shared.api_core.client.bootstrap_safety:CODE:legacy_failover:20260426⬡
// ⬡B:gmgu:PATCH:gap3.9_air_timeout_retry_wire:20260522⬡
// Gap 3.9 — import airWithRetry from airTimeout.js.
// air() wraps its fetch in Promise.race via airWithRetry (AIR_TIMEOUT=55000,
// AIR_RETRY_COUNT=2, AIR_RETRY_DELAY=5000). On network error or timeout,
// retries up to AIR_RETRY_COUNT before throwing. 4xx responses not retried.
import { airWithRetry, AIR_TIMEOUT, AIR_RETRY_COUNT, AIR_RETRY_DELAY } from './airTimeout.js';
// API adapter for calling A'NEW from any surface (standalone, CIB, CIP).
// This is the ONE place that resolves the backend URL. Every app imports from here.
//
// Bootstrap safety (added 20260426): the air() method falls over from the primary
// ababase backend to the legacy abacia-services backend when primary is unreachable
// or returns 5xx. Brandon's bootstrap paradox: CCWA codes ababase, so a CCWA-driven
// bad deploy can break ababase, and ababase being down would strand CCWA without
// thinking. Failover keeps the conversation alive on legacy until prod is fixed.
//
// Failover applies ONLY to /api/air/process (the conversation critical path).
// Other methods (findContext, writeChatMessage, writeInteraction, findBySource)
// are ababase-only — they fail honestly when ababase is down. Those are not
// in the critical "Brandon must be able to talk to ABA" path.

export const DEFAULT_ANEW_API_BASE = 'https://aibebase.onrender.com';
const DEFAULT_DEV = 'https://incuaba.onrender.com';

export function resolveAnewApiBase({ baseUrl, windowRef, env } = {}) {
  const browser = windowRef === undefined
    ? (typeof window !== 'undefined' ? window : null)
    : windowRef;
  const buildEnv = env === undefined
    ? (typeof import.meta !== 'undefined' ? import.meta.env : null)
    : env;
  const selected = baseUrl
    || (browser && browser.__ANU_MIND_BASE)
    || (buildEnv && buildEnv.VITE_ANEW_URL)
    || DEFAULT_ANEW_API_BASE;
  return String(selected).trim().replace(/\/+$/, '');
}

export function resolveAnewWebSocketBase(options = {}) {
  return resolveAnewApiBase(options).replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
}

// ⬡B:aba_shared.api_core.client.HARDCODED_LEGACY_DEP:REMOVED:shutdown_executed:20260708⬡
// The abacia-services failover this comment warned about was removed
// 20260708, exactly as this comment instructed, because abacia-services was
// suspended and stripped of its credentials during a real security response
// (see the client.LEGACY_FAILOVER_REMOVED comment further down for the full
// account). This block is kept as the historical record of the warning
// being heeded, not bypassed.

// Primary attempt budget before failing over. ⬡B:aba_shared.api_core.client.primary_timeout:CONST:recalibrated_air_latency:20260508⬡
// Recalibrated 2026-05-08 from 5000 to 45000ms. Root cause of P0 dc870a3c
// chat-doesn't-respond: AIR routinely takes 5-15s on the gemini-3.1-pro path
// (77 agents loaded, FCW build, WRIT enforcement, tool loop) and Render cold
// start can add another ~30s. The previous 5s budget conflated "primary slow"
// with "primary down" and silently failed every chat over to legacy
// ⬡B:aba_shared.api_core.client.fetchWithFailover:REMOVED:confirmed_orphan_zero_callers:20260708⬡
// fetchWithFailover, composeAbortSignals, bumpFailoverCounter, and
// PRIMARY_TIMEOUT_MS were removed 20260708. Verified before removal: zero
// call sites anywhere in this file or the wider monorepo (the ONE caller,
// air(), used its own separate inline retry+failover logic, since fixed
// separately in the LEGACY_FAILOVER_REMOVED block below). This function had
// been dead code since before this repository's earliest commit this
// session could trace — a real orphan, the exact class of bug this
// ecosystem's roadmap discipline exists to catch, left in place rather than
// deleted because nothing ever broke loudly enough to force the question.
// ⬡B:aba_shared.api_core.client.dead_failover_block:REMOVED:confirmed_zero_callers:20260708⬡
// fetchWithFailover, composeAbortSignals, and bumpFailoverCounter removed here.
// Verified before removal: zero call sites anywhere in this file or the wider
// monorepo. air() (below) used its own separate inline retry+failover logic,
// independently fixed in the LEGACY_FAILOVER_REMOVED block near air() itself.
// This was dead code left in place because nothing ever broke loudly enough
// to force the question -- exactly the orphan class this ecosystem's roadmap
// discipline exists to catch. Full removal, not a stub: no dead body kept.

/**
 * Create an ABA API client bound to a HAM identity.
 *
 * @param {object} config
 * @param {string} config.baseUrl - explicit A'NEW base; otherwise the canonical runtime resolver is used
 * @param {object} config.hamHint - must contain aclUid OR email OR phone OR slug. Refused if empty.
 * @param {string} config.sessionId - client-supplied session id for chat threading
 * @param {boolean} config.disableFailover - if true, air() does NOT fail over to legacy. Defaults false.
 */
export function createAbaClient({ baseUrl, hamHint, sessionId, disableFailover = false } = {}) {
  if (!hamHint || typeof hamHint !== 'object' || Object.keys(hamHint).length === 0) {
    throw new Error('createAbaClient: hamHint required (aclUid, email, phone, slug, or name). No silent defaults.');
  }
  const base = resolveAnewApiBase({ baseUrl });
  const session = sessionId || null;

  async function post(path, body, opts = {}) {
    const res = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
      body: JSON.stringify(body),
      signal: opts.signal
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || `HTTP ${res.status}`);
      err.code = data.code;
      err.status = res.status;
      err.detail = data.detail;
      throw err;
    }
    return data;
  }

  async function get(path, opts = {}) {
    const res = await fetch(`${base}${path}`, { method: 'GET', signal: opts.signal });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || `HTTP ${res.status}`);
      err.code = data.code;
      err.status = res.status;
      throw err;
    }
    return data;
  }

  // The raw Response, for streamed (SSE) answers the caller reads itself, such as MESA's COOK.
  async function postRaw(path, body, opts = {}) {
    return fetch(`${base}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
      body: JSON.stringify(body),
      signal: opts.signal
    });
  }

  return {
    baseUrl: base,
    hamHint,
    sessionId: session,
    disableFailover,

    // The mounted MESA and IRIS hooks call these directly (TIM, summary, save, COOK; IRIS's job list).
    post,
    postRaw,
    get,

    // AIR entry point — main conversation turn.
    // ⬡B:gmgu:PATCH:gap3.9_air_timeout_retry_wire:20260522⬡
    // Gap 3.9: wraps fetch in airWithRetry (AIR_TIMEOUT=55000, AIR_RETRY_COUNT=2,
    // AIR_RETRY_DELAY=5000). On network error or timeout, retries up to
    // AIR_RETRY_COUNT before throwing. 4xx responses surface to caller immediately.
    // Gap 5.3: accepts previousMessages[] and passes it in the request body so
    // GURU sees full conversation context across turns.
    // Uses fetchWithFailover when disableFailover is false (default) so the
    // conversation stays alive even if ababase prod is down.
    async air({ message, channel = 'ccwa', agent = null, tags = [], signal, sessionId: sessionIdOverride, previousMessages } = {}) {
      // ⬡B:aba_shared.api_core.client.air.session_override:CODE:per_call_session_override:20260508⬡
      // sessionIdOverride lets per-tab callers pass tab.sessionId without
      // restructuring the client. Falls back to the closure session captured
      // at createAbaClient time. Brain: rally.claude_base.envolve.intent_boundclient_override_param_implementation.20260508
      const path = '/api/air/process';
      const bodyObj = {
        message, hamHint, channel, agent, tags, sessionId: sessionIdOverride || session
      };
      // Gap 5.3 — previousMessages: only include when non-empty
      if (Array.isArray(previousMessages) && previousMessages.length > 0) {
        bodyObj.previousMessages = previousMessages;
      }
      const init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyObj),
        signal
      };

      // Gap 3.9: use airWithRetry for timeout + retry.
      //
      // ⬡B:aba_shared.api_core.client.LEGACY_FAILOVER_REMOVED:FIX:dead_host_no_longer_rerouted:20260708⬡
      // The legacy failover to abacia-services was removed 20260708. That
      // service was suspended and stripped of its own credentials during a
      // real security response (a Gemini key it held had no legitimate
      // caller left on that host) and now answers every request with a flat
      // 503. This file's own 20260426 comment predicted exactly this
      // scenario and instructed: "if you are shutting down abacia-services,
      // you MUST update this value or remove the failover entirely." A
      // silent reroute to a guaranteed-503 host is worse than an honest
      // failure — the caller was getting a confusing 503 from a host it
      // never asked about, with no signal that failover even happened
      // beyond a console.warn nobody watching the UI would see. Doctrine:
      // silence-or-real-answer, never a hollow reroute standing in for one.
      // airWithRetry's own timeout+retry budget IS the real resilience here;
      // when that's exhausted, the honest answer is "primary unreachable,"
      // thrown as a real error the caller can display, not masked by a
      // second dead host's response.
      let res;
      res = await airWithRetry(`${base}${path}`, init, {
        timeoutMs: AIR_TIMEOUT,
        retryCount: AIR_RETRY_COUNT,
        retryDelay: AIR_RETRY_DELAY,
      });
      if (!disableFailover && res.status >= 500) {
        // Primary reachable but erroring. No legacy host to fall to anymore;
        // surface the real status rather than a fake success.
        console.warn('[aba-api-core] primary returned', res.status, 'after retries, no legacy failover remains (removed 20260708, see client.js header)');
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.error || `HTTP ${res.status}`);
        err.code = data.code;
        err.status = res.status;
        err.detail = data.detail;
        throw err;
      }
      return data;
    },

    // ⬡B:aba_shared.api_core.client.airStream:CODE:sse_for_long_runs_no_network_timeout:20260512⬡
    // SSE-based AIR entry. Use this for any run that may exceed ~60 seconds.
    // The synchronous air() endpoint hits HTTP-layer timeouts on long runs;
    // /api/air/stream streams named phases (agents-working, attempts, result)
    // so the client gets progress as it happens and the connection never
    // single-hangs. Same payload shape as air(). Optional onPhase callback
    // fires for each SSE event with { event, data }. Returns the final
    // result payload (the SSE 'result' event's data) so callers can treat
    // airStream() and air() interchangeably as a promise.
    async airStream({ message, channel = 'ccwa', agent = null, tags = [], signal, sessionId: sessionIdOverride, onPhase } = {}) {
      const path = '/api/air/stream';
      const init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, hamHint, channel, agent, tags, sessionId: sessionIdOverride || session }),
        signal
      };
      const res = await fetch(`${base}${path}`, init);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        const err = new Error('airStream HTTP ' + res.status);
        err.status = res.status;
        err.detail = text.slice(0, 300);
        throw err;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE blocks separated by blank lines
        let blockEnd;
        while ((blockEnd = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, blockEnd);
          buffer = buffer.slice(blockEnd + 2);
          let event = 'message';
          let dataStr = '';
          for (const line of block.split('\n')) {
            if (line.startsWith('event: ')) event = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr += line.slice(6);
          }
          if (!dataStr) continue;
          let payload;
          try { payload = JSON.parse(dataStr); } catch { payload = { raw: dataStr }; }
          if (typeof onPhase === 'function') {
            try { onPhase({ event, data: payload }); } catch {}
          }
          if (event === 'result') finalResult = payload;
        }
      }
      if (!finalResult) throw new Error('airStream: no result event received');
      return finalResult;
    },

    // Read the HAM's full context bundle in one query
    async findContext({ channel = 'ccwa', agent = null, messageLimit = 20, interactionLimit = 5, omiLimit = 5, memoryLimit = 10 } = {}) {
      if (!hamHint.aclUid) {
        throw new Error('findContext: aclUid required on hamHint for context read');
      }
      return post('/api/find/context', {
        hamUid: hamHint.aclUid,
        channel,
        agent,
        messageLimit,
        interactionLimit,
        omiLimit,
        memoryLimit
      });
    },

    // Write chat message to the HAM's private scope
    async writeChatMessage({ channel = 'ccwa', role, content, agentGlobal = null, metadata = {}, sessionId: sessionIdOverride } = {}) {
      // ⬡B:aba_shared.api_core.client.writeChatMessage.session_override:CODE:per_call_session_override:20260508⬡
      return post('/api/ham/chat', {
        hamHint,
        sessionId: sessionIdOverride || session,
        channel,
        role,
        content,
        agentGlobal,
        metadata
      });
    },

    // Log an agent_interaction row
    async writeInteraction({ agentGlobal, interactionType, inputRef = null, outputSummary = null, metadata = {}, keepLastN = 50, sessionId: sessionIdOverride } = {}) {
      // ⬡B:aba_shared.api_core.client.writeInteraction.session_override:CODE:per_call_session_override:20260508⬡
      // inputRef precedence preserved: explicit inputRef > sessionIdOverride > closure session.
      return post('/api/ham/interaction', {
        hamHint,
        agentGlobal,
        interactionType,
        inputRef: inputRef || sessionIdOverride || session,
        outputSummary,
        metadata,
        keepLastN
      });
    },

    // Read a node by source key
    async findBySource(source) {
      const encoded = encodeURIComponent(source);
      return get(`/api/find/by-source?source=${encoded}`);
    },

    // Health probe used by surfaces to verify backend reachable before rendering
    async health() { return get('/health'); },

    // ⬡B:aba_shared.api_core.client.gmgu:CODE:rally.mace.gmgu_curriculum_endpoint.20260520⬡
    // GMG University namespace. getNextLessons() reads the per-HAM curriculum
    // track from the brain via GET /api/gmgu/curriculum?hamUid={aclUid}.
    // No args needed — hamHint.aclUid is the only signal required.
    gmgu: {
      async getNextLessons() {
        if (!hamHint.aclUid) {
          throw new Error('gmgu.getNextLessons: aclUid required on hamHint');
        }
        return get(`/api/gmgu/curriculum?hamUid=${encodeURIComponent(hamHint.aclUid)}`);
      },
    },
  };
}

// Exported for testing and ops surfaces.
// ⬡B:aba_shared.api_core.client.internals_export_fixed:FIX:dangling_refs_removed:20260708⬡
// fetchWithFailover, composeAbortSignals, LEGACY_FAILOVER_BASE, and
// PRIMARY_TIMEOUT_MS were removed above; this export block referenced all
// four by name, which would throw ReferenceError at module load the moment
// anything imported __internals. Caught before shipping by a real bundle
// build, not left for a runtime crash to find.
export const __internals = {};
